<script lang="ts">
  import { router } from '@inertiajs/svelte';

  type Option = { value: string; label: string; weight?: number };
  type Question = {
    id: string;
    type: 'single' | 'multi' | 'text' | 'matrix';
    label: string;
    description?: string;
    options?: Option[];
    rows?: string[];
    cols?: string[];
    row_keys?: string[];
    col_keys?: string[];
  };

  interface ResponseData {
    id: string;
    respondent_badge: string;
    respondent_name: string | null;
    threads_username: string;
    email: string | null;
    computed_tier: string;
    tier_label: string;
    computed_score: number;
    interests: string[];
    submitted_at: string | null;
    created_at: string;
    supersedes_response_id: string | null;
    answers: Record<string, string | string[]>;
  }

  let {
    survey, response, tier_hint, tier_hex
  }: {
    survey: { id: string; slug: string; title: string; questions: Question[] };
    response: ResponseData;
    tier_hint: string | null;
    tier_hex: string | null;
  } = $props();

  function optionLabel(q: Question, value: string): string {
    const opt = q.options?.find(o => o.value === value);
    return opt?.label ?? value;
  }

  function renderAnswer(q: Question): { type: string; value: unknown } {
    const ans = response.answers[q.id];
    if (ans === undefined || ans === null || ans === '') {
      return { type: 'empty', value: null };
    }

    if (q.type === 'single') {
      return { type: 'single', value: optionLabel(q, ans as string) };
    }
    if (q.type === 'multi') {
      const arr = Array.isArray(ans) ? ans : [ans];
      return { type: 'multi', value: arr.map(v => optionLabel(q, v as string)) };
    }
    if (q.type === 'matrix') {
      // ans = ["mon_morning", "tue_afternoon", ...]
      const arr = Array.isArray(ans) ? ans : [ans];
      const byRow: Record<string, string[]> = {};
      for (const item of arr) {
        const idx = (item as string).lastIndexOf('_');
        if (idx < 0) continue;
        const rowKey = (item as string).slice(0, idx);
        const colKey = (item as string).slice(idx + 1);
        const rowLabel = q.row_keys && q.rows
          ? q.rows[q.row_keys.indexOf(rowKey)] ?? rowKey
          : rowKey;
        const colLabel = q.col_keys && q.cols
          ? (q.cols[q.col_keys.indexOf(colKey)] ?? colKey).replace(/\n/g, ' ')
          : colKey;
        byRow[rowLabel] = byRow[rowLabel] || [];
        byRow[rowLabel].push(colLabel);
      }
      return { type: 'matrix', value: byRow };
    }
    if (q.type === 'text') {
      return { type: 'text', value: ans };
    }
    return { type: 'raw', value: ans };
  }

  function destroyResponse() {
    if (!confirm('정말 이 응답을 삭제하시겠습니까?')) return;
    router.delete(`/admin/community/surveys/${survey.slug}/responses/${response.id}`);
  }
</script>

<svelte:head><title>{response.respondent_name ?? response.respondent_badge} — 응답 상세</title></svelte:head>

<div class="p-6 max-w-4xl mx-auto space-y-6">
  <!-- Header -->
  <header>
    <a href="/admin/community/surveys/{survey.slug}/responses"
       class="text-sm text-gray-600 hover:underline">← 응답 목록</a>
    <div class="flex items-start justify-between mt-2 gap-4">
      <div>
        <h1 class="text-2xl font-bold">
          {response.respondent_name ?? '이름 없음'}
        </h1>
        <p class="text-sm text-gray-500 mt-1">
          {response.threads_username ? `@${response.threads_username}` : ''}
          {response.email ? ` · ${response.email}` : ''}
        </p>
      </div>
      <button onclick={destroyResponse}
              class="text-sm text-red-600 hover:bg-red-50 px-3 py-1.5 rounded border border-red-200">
        삭제
      </button>
    </div>
  </header>

  <!-- Summary -->
  <div class="grid grid-cols-3 gap-4">
    <div class="p-4 border rounded bg-white">
      <div class="text-xs text-gray-500">자동 분류 (참고용)</div>
      <div class="mt-1 flex items-center gap-2">
        <span class="inline-block w-3 h-3 rounded-full"
              style="background: {tier_hex ?? '#999'}"></span>
        <span class="font-bold">{response.tier_label}</span>
      </div>
      {#if tier_hint}
        <div class="text-xs text-gray-500 mt-1">{tier_hint}</div>
      {/if}
    </div>
    <div class="p-4 border rounded bg-white">
      <div class="text-xs text-gray-500">점수</div>
      <div class="text-2xl font-bold">{response.computed_score}</div>
    </div>
    <div class="p-4 border rounded bg-white">
      <div class="text-xs text-gray-500">제출 시각</div>
      <div class="text-sm mt-1">
        {response.submitted_at
          ? new Date(response.submitted_at).toLocaleString('ko')
          : new Date(response.created_at).toLocaleString('ko')}
      </div>
    </div>
  </div>

  <!-- Badge + Phone -->
  <div class="p-4 bg-gray-50 border rounded flex flex-wrap gap-x-8 gap-y-2 text-sm">
    <div>
      <span class="text-gray-500">뱃지:</span>
      <span class="font-mono ml-1">{response.respondent_badge}</span>
    </div>
    {#if response.answers.phone}
      <div>
        <span class="text-gray-500">휴대폰:</span>
        <a href="tel:{response.answers.phone}" class="ml-1 text-blue-600 hover:underline">
          {response.answers.phone}
        </a>
      </div>
    {/if}
    {#if response.supersedes_response_id}
      <div class="text-xs text-gray-500">🔁 이전 응답 수정본</div>
    {/if}
  </div>

  <!-- Answers -->
  <section class="space-y-4">
    <h2 class="text-lg font-bold border-b pb-2">설문 응답</h2>

    {#each survey.questions as q (q.id)}
      {@const rendered = renderAnswer(q)}
      {#if q.id === 'phone'}
        <!-- skip phone, shown above -->
      {:else}
        <div class="p-4 border rounded bg-white">
          <div class="text-sm font-semibold text-gray-700">{q.label}</div>
          {#if q.description}
            <div class="text-xs text-gray-500 mt-0.5">{q.description}</div>
          {/if}

          <div class="mt-2">
            {#if rendered.type === 'empty'}
              <span class="text-xs text-gray-400 italic">응답 없음</span>
            {:else if rendered.type === 'single'}
              <span class="inline-block px-2 py-1 bg-blue-50 text-blue-800 text-sm rounded">
                {rendered.value}
              </span>
            {:else if rendered.type === 'multi'}
              <div class="flex flex-wrap gap-2">
                {#each rendered.value as v}
                  <span class="px-2 py-1 bg-blue-50 text-blue-800 text-sm rounded">{v}</span>
                {/each}
              </div>
            {:else if rendered.type === 'matrix'}
              <div class="space-y-1">
                {#each Object.entries(rendered.value as Record<string, string[]>) as [row, times]}
                  <div class="text-sm">
                    <span class="font-medium text-gray-700">{row}요일:</span>
                    <span class="text-gray-600 ml-1">{times.join(' · ')}</span>
                  </div>
                {/each}
              </div>
            {:else if rendered.type === 'text'}
              <p class="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                {rendered.value}
              </p>
            {:else}
              <pre class="text-xs text-gray-600">{JSON.stringify(rendered.value, null, 2)}</pre>
            {/if}
          </div>
        </div>
      {/if}
    {/each}
  </section>
</div>
